import React, { useCallback } from 'react';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Loading } from '@swyftx/aviary/icons/outlined';
import { EnhancedTable, EnhancedTableData } from '@swyftx/aviary/molecules/EnhancedTable';
import { MarginTransaction } from '@swyftx/margin/build/types';

import { Modals } from '@global-components/Modals/Modals.enum';
import { useModal } from '@global-components/Modals/useModal.hooks';

import { MarginBalanceAdjustmentsTableData } from './MarginBalanceAdjustmentsTable.data';
import { MarginBalanceAdjustmentsTableEmpty } from './components';
import { useMarginBalanceAdjustmentsTable } from './hooks';

type Props = {
  className?: string;
  hideHeader?: boolean;
  groupBy?: keyof MarginBalanceAdjustmentsTableData;
  sortKey?: keyof MarginBalanceAdjustmentsTableData;
  isDesktopView?: boolean;
  isSm?: boolean;
  isXs?: boolean;
};

const MarginBalanceAdjustmentsTable: React.FC<Props> = ({
  className,
  hideHeader = false,
  groupBy,
  sortKey,
  isDesktopView,
  isSm,
  isXs,
}) => {
  const {
    headers,
    data,
    onSort,
    initialSort,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    isError,
    fetchNextPage,
  } = useMarginBalanceAdjustmentsTable({
    sortKey,
    isDesktopView,
    isSm,
    isXs,
  });
  const { openModal } = useModal();

  const handleOnClickItem = useCallback(
    (item: EnhancedTableData<MarginBalanceAdjustmentsTableData, MarginTransaction>) => {
      openModal(Modals.MarginBalanceAdjustmentDetails, { transactionId: item.value?.transactionId });
    },
    [openModal],
  );

  return (
    <EnhancedTable<MarginBalanceAdjustmentsTableData, MarginTransaction>
      data-container='orders-history-table'
      headers={headers}
      data={data}
      onSort={onSort}
      sort={initialSort}
      headerClassName='bg-color-background-surface-tertiary'
      inset
      rowClassName='h-16'
      itemsPerPage={8}
      className={className}
      onClickItem={handleOnClickItem}
      hideHeader={hideHeader}
      groupBy={data.length > 0 ? groupBy : undefined}
      onFetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isLoading={isLoading || isFetching || isFetchingNextPage}
      lazyLoad
    >
      {isLoading ? (
        <FlexLayout className='h-full w-full' alignItems='center' justifyContent='center'>
          <Loading className='animate-spin' />
        </FlexLayout>
      ) : (
        !data.length && <MarginBalanceAdjustmentsTableEmpty isError={isError} />
      )}
    </EnhancedTable>
  );
};

export { MarginBalanceAdjustmentsTable };
